import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { themeVar as colors, type as typeTokens } from "../design-tokens";
import { getEditableReport, patchReport, type EditableReportFields } from "./api";

/**
 * What the screen around this editor can ask it to do (2026-08-10).
 *
 * `save()` exists so the download controls ABOVE this editor can offer "save, then download"
 * instead of merely refusing. It resolves `true` only when the server accepted the patch — on a
 * refusal it resolves `false` and this component keeps the agent's text and shows the server's own
 * sentence, so the caller never has to invent an error message it does not have.
 *
 * Nothing to save is `true`, not a failure: "make sure my edits are in before you download" is
 * satisfied when there are no edits.
 */
export interface ReportEditorHandle {
  save: () => Promise<boolean>;
}

/**
 * EDIT THE FINISHED BRIEF — the prose, and only the prose (2026-08-01).
 *
 * Every box here is a field `server/amb_edit.py` will accept back; there is deliberately no control
 * for anything it would refuse. An agent typing a careful correction into a box that was never
 * going to save it is worse than not offering the box, so the field list is fetched from the server
 * rather than declared twice.
 *
 * What is NOT here, and why: the estimate, the range, the suggested list, the adjustment grid, the
 * per-comp adjusted values. Those are the engine's output. If a figure is wrong the fix is upstream
 * in the review grid, where the correction is attributed and the estimate is recomputed from it —
 * not typed over in the finished document. The moment someone can hand-edit the number, "every
 * figure traces to its source" is no longer true, and that promise is the product.
 *
 * The number gate is the server's, re-run over the edited text against the same allow-list the
 * model's draft was checked against. So a refusal here names a real figure ("your edit mentions
 * $430,000, and no figure in this report supports it") rather than a validation code, and this
 * component's job is just to show it next to the box the agent is looking at.
 */
export const ReportEditor = forwardRef<ReportEditorHandle, {
  jobId: string;
  /** Called after a successful save so the caller can re-fetch and re-render the report — the
   * download endpoints re-render from the same stored dict, so the preview and the PDF cannot
   * disagree once this has run. */
  onSaved: () => void;
  onCancel: () => void;
  /** THE UNSAVED-EDIT TRAP (2026-08-10). Reported to the screen so the Download PDF / Download Word
   * links an inch above this editor can stop being live-and-unguarded while there is typed text
   * that has not been sent. Fires on every transition, and fires `false` on unmount, so a caller
   * that closes the editor is never left holding a stale `true` and warning about nothing. */
  onDirtyChange?: (dirty: boolean) => void;
}>(function ReportEditor({ jobId, onSaved, onCancel, onDirtyChange }, ref) {
  const [fields, setFields] = useState<EditableReportFields | null>(null);
  const [cardNames, setCardNames] = useState<string[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [original, setOriginal] = useState<EditableReportFields | null>(null);

  useEffect(() => {
    let cancelled = false;
    getEditableReport(jobId)
      .then((r) => {
        if (cancelled) return;
        setFields(r.fields);
        setOriginal(r.fields);
        setCardNames(r.card_names ?? []);
      })
      .catch((e) => {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : "Couldn't load the editable text.");
      });
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  /** Only what actually changed goes over the wire. Sending the whole document back would make an
   * untouched paragraph look edited and would put every field through the figure gate on a save
   * that only rewrote one sentence. */
  const patch = useMemo(() => {
    if (!fields || !original) return {};
    const out: Record<string, unknown> = {};
    (["lead", "about", "sitting", "how_reached", "pricing_rationale"] as const).forEach((k) => {
      if (fields[k] !== original[k]) out[k] = fields[k];
    });
    (["neighborhood", "note"] as const).forEach((k) => {
      if (JSON.stringify(fields[k]) !== JSON.stringify(original[k])) out[k] = fields[k];
    });
    const blurbs: Record<string, string> = {};
    for (const [i, v] of Object.entries(fields.card_blurbs)) {
      if (original.card_blurbs[i] !== v) blurbs[i] = v;
    }
    if (Object.keys(blurbs).length) out.card_blurbs = blurbs;
    return out;
  }, [fields, original]);

  const dirty = Object.keys(patch).length > 0;

  /** Held in a ref so the effects below depend on `dirty` alone. A caller passing an inline arrow
   * would otherwise change this prop's identity on every render and re-run the unmount cleanup,
   * which reports `false` — a warning that flickered off mid-keystroke would be worse than none. */
  const dirtyCb = useRef(onDirtyChange);
  dirtyCb.current = onDirtyChange;

  useEffect(() => {
    dirtyCb.current?.(dirty);
  }, [dirty]);

  // Closing the editor — saved, discarded, or the whole screen replaced — always ends with the
  // caller told there is nothing outstanding.
  useEffect(() => () => dirtyCb.current?.(false), []);

  /* NAVIGATING AWAY MID-EDIT. The browser's own "leave site?" prompt is the only thing that can
   * catch a reload, a closed tab or a typed URL, and it is only armed while there is something to
   * lose — a page that always asks is a page whose question stops being read. Chrome and Safari
   * still require `returnValue` to be set, not just `preventDefault()`. */
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  /** A second press of a destructive button, not a `window.confirm`. Inline, in place, and it
   * disappears the moment there is nothing to discard — see the render site. */
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  useEffect(() => {
    if (!dirty) setConfirmingDiscard(false);
  }, [dirty]);

  /** Resolves `true` only if the server took it. See `ReportEditorHandle` — the download guard
   * above this editor calls exactly this, so "save, then download" and pressing "Save the changes"
   * cannot diverge into two code paths that behave differently. */
  async function handleSave(): Promise<boolean> {
    if (!dirty) return true;
    setSaving(true);
    setSaveError(null);
    try {
      await patchReport(jobId, patch);
      // WHAT IS ON SCREEN IS NOW WHAT IS STORED, so the baseline the diff is taken against moves
      // with it. Today the screen unmounts this editor on `onSaved`, which hides the difference —
      // but the dirty flag is what arms the reload guard and the download warning, and leaving it
      // stuck on `true` after a successful save would mean those fire over nothing the moment
      // anyone decides to keep the editor open. Order matters: set the baseline BEFORE handing
      // control back, so no listener ever observes a saved editor calling itself dirty.
      setOriginal(fields);
      onSaved();
      return true;
    } catch (e) {
      setSaveError(
        e instanceof Error && e.message
          ? e.message
          : "That edit couldn't be saved. Nothing was changed — the brief is exactly as it was."
      );
      return false;
    } finally {
      setSaving(false);
    }
  }

  useImperativeHandle(ref, () => ({ save: handleSave }));

  if (loadError) {
    return (
      <div role="alert" style={panel(colors.cautionBg, colors.caution)}>
        <p style={{ margin: 0, fontSize: 13 }}>
          Couldn&apos;t open the editable text ({loadError}). The report itself is untouched, and the
          PDF and Word downloads above still work.
        </p>
        <button type="button" onClick={onCancel} style={secondaryButton}>
          Close
        </button>
      </div>
    );
  }

  if (!fields) {
    return <p style={{ marginTop: 16, fontSize: 13, color: colors.textSecondary }}>Loading the text…</p>;
  }

  const set = (patchIn: Partial<EditableReportFields>) => setFields({ ...fields, ...patchIn });

  return (
    <div style={{ marginTop: 20, fontFamily: typeTokens.fontUi }}>
      <div style={panel(colors.accentSoft, colors.border)}>
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5 }}>
          <strong>You&apos;re editing the writing, not the figures.</strong> The estimate, the range,
          the suggested list and every comparable&apos;s adjusted value stay exactly as the
          comparables produced them. If a number is wrong, go back to the review grid and correct it
          there so the estimate is recomputed from your correction.
        </p>
        <p style={{ margin: "8px 0 0", fontSize: 12, color: colors.textSecondary, lineHeight: 1.5 }}>
          Your text is saved as plain text — formatting marks won&apos;t carry through. A figure that
          isn&apos;t supported by the comparables will be refused, and named, rather than printed.
        </p>
      </div>

      <Field
        label="Opening"
        hint="The paragraph the client reads first."
        value={fields.lead}
        onChange={(v) => set({ lead: v })}
        rows={4}
      />
      <Field
        label="About this home"
        value={fields.about}
        onChange={(v) => set({ about: v })}
        rows={4}
      />
      <ListField
        label="The comparable set"
        hint="One box per paragraph. Empty a box to drop that paragraph."
        values={fields.neighborhood}
        onChange={(v) => set({ neighborhood: v })}
      />

      {Object.keys(fields.card_blurbs).length > 0 && (
        <section style={{ marginTop: 22 }}>
          <h3 style={sectionHeading}>What each comparable says</h3>
          {Object.entries(fields.card_blurbs).map(([idx, blurb]) => (
            <Field
              key={idx}
              label={cardNames[Number(idx)] || `Comparable ${Number(idx) + 1}`}
              value={blurb}
              onChange={(v) => set({ card_blurbs: { ...fields.card_blurbs, [idx]: v } })}
              rows={3}
            />
          ))}
        </section>
      )}

      <Field label="What is sitting" value={fields.sitting} onChange={(v) => set({ sitting: v })} rows={3} />
      <Field
        label="How I reached this figure"
        value={fields.how_reached}
        onChange={(v) => set({ how_reached: v })}
        rows={4}
      />
      <Field
        label="Pricing rationale"
        hint="The reasoning under the three prices — the prices themselves aren't editable."
        value={fields.pricing_rationale}
        onChange={(v) => set({ pricing_rationale: v })}
        rows={4}
      />
      <ListField
        label="Agent note"
        hint="Your letter to the client. One box per paragraph."
        values={fields.note}
        onChange={(v) => set({ note: v })}
      />

      {saveError && (
        <p role="alert" style={{ marginTop: 16, padding: "12px 14px", borderRadius: 10, background: colors.cautionBg, border: `1px solid ${colors.cautionLine}`, fontSize: 13, color: colors.caution, lineHeight: 1.5 }}>
          {saveError}
        </p>
      )}

      <div style={{ marginTop: 18, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <button className="vb-btn"
          type="button"
          onClick={handleSave}
          disabled={!dirty || saving}
          aria-disabled={!dirty || saving}
          style={{
            padding: "10px 18px", borderRadius: 8, border: "none",
            background: dirty && !saving ? colors.verified : colors.neutralChip,
            color: dirty && !saving ? "#fff" : colors.textSecondary,
            fontWeight: 600, fontSize: 13,
            cursor: dirty && !saving ? "pointer" : "not-allowed",
          }}
        >
          {saving ? "Saving…" : "Save the changes"}
        </button>
        {/* DISCARD ASKS ONCE, AND ONLY WHEN THERE IS SOMETHING TO LOSE. Clean, this is "Done" and
            closes on the first press — confirming a no-op is the nag that teaches people to click
            through confirmations. Dirty, the button turns into the question in place, so the
            answer is given at the same spot the mistake would have been made. */}
        {confirmingDiscard ? (
          <span
            role="alert"
            style={{ display: "inline-flex", gap: 10, alignItems: "center", flexWrap: "wrap", fontSize: 13 }}
          >
            <span>Throw away your unsaved edits?</span>
            <button className="vb-btn"
              type="button"
              onClick={onCancel}
              style={{ ...secondaryButton, borderColor: colors.caution, color: colors.caution }}
            >
              Yes, discard them
            </button>
            <button type="button" onClick={() => setConfirmingDiscard(false)} style={secondaryButton}>
              Keep editing
            </button>
          </span>
        ) : (
          <button
            type="button"
            onClick={() => (dirty ? setConfirmingDiscard(true) : onCancel())}
            style={secondaryButton}
          >
            {dirty ? "Discard the changes" : "Done"}
          </button>
        )}
        {dirty && !saving && !confirmingDiscard && (
          <span style={{ fontSize: 12, color: colors.textSecondary }}>
            Not saved yet — the PDF and Word downloads above still hold the previous wording.
          </span>
        )}
      </div>
    </div>
  );
});

function Field({
  label,
  hint,
  value,
  onChange,
  rows,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
  rows: number;
}) {
  return (
    <section style={{ marginTop: 18 }}>
      <label style={{ display: "block", fontSize: 13, fontWeight: 600 }}>{label}</label>
      {hint && <p style={{ margin: "2px 0 6px", fontSize: 11, color: colors.textSecondary }}>{hint}</p>}
      <textarea
        aria-label={label}
        value={value}
        rows={rows}
        onChange={(e) => onChange(e.target.value)}
        style={textareaStyle}
      />
    </section>
  );
}

function ListField({
  label,
  hint,
  values,
  onChange,
}: {
  label: string;
  hint?: string;
  values: string[];
  onChange: (v: string[]) => void;
}) {
  return (
    <section style={{ marginTop: 18 }}>
      <label style={{ display: "block", fontSize: 13, fontWeight: 600 }}>{label}</label>
      {hint && <p style={{ margin: "2px 0 6px", fontSize: 11, color: colors.textSecondary }}>{hint}</p>}
      {values.length === 0 && (
        <p style={{ margin: "2px 0 6px", fontSize: 12, color: colors.textSecondary }}>
          This section is empty in the report.
        </p>
      )}
      {values.map((v, i) => (
        <textarea
          key={i}
          aria-label={`${label} paragraph ${i + 1}`}
          value={v}
          rows={3}
          onChange={(e) => {
            const next = values.slice();
            next[i] = e.target.value;
            onChange(next);
          }}
          style={{ ...textareaStyle, marginTop: i === 0 ? 0 : 8 }}
        />
      ))}
    </section>
  );
}

const textareaStyle: React.CSSProperties = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 8,
  border: `1px solid ${colors.border}`,
  fontFamily: typeTokens.fontUi,
  fontSize: 13,
  lineHeight: 1.6,
  resize: "vertical",
  boxSizing: "border-box",
  background: colors.bgSurface,
};

const sectionHeading: React.CSSProperties = {
  fontSize: 13,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: colors.textSecondary,
  marginBottom: 2,
};

const secondaryButton: React.CSSProperties = {
  padding: "10px 18px",
  borderRadius: 8,
  border: `1px solid ${colors.border}`,
  background: colors.bgSurface,
  color: colors.textPrimary,
  fontSize: 13,
  cursor: "pointer",
};

function panel(background: string, border: string): React.CSSProperties {
  return { padding: "12px 14px", borderRadius: 10, background, border: `1px solid ${border}` };
}
